import React, { useEffect, useState } from "react";
import T from "prop-types";
import Loader from "commons/components/Loader";
import Tag from "commons/components/Tag";
import { useTagGroups, useTagGroupsDispatch } from "App/contexts/TagGroupsContext";
import callLocalStorage from "commons/util/callLocalStorage";
import { getNextId, getTagGroupColor } from "commons/util/helpers";
import callApi from "commons/util/callApi";
import TagInput from "./TagInput";

function TagInputContainer({ selectedTags, setSelectedTags }) {
  const [searchPhrase, setSearchPhrase] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);

  const { tagGroups, isLoading } = useTagGroups();
  const dispatch = useTagGroupsDispatch();

  function transformTagGroupsIntoTagOptions(tagGroups) {
    return tagGroups
      .map(el => {
        const rememberedIsExpanded = callLocalStorage(`tag-group-panel-expanded-${el.id}`, "get");
        return {
          id: el.id,
          label: el.label,
          items: el.tags
            .filter(t => (searchPhrase ? t.label.toLowerCase().includes(searchPhrase.toLowerCase()) : t))
            .map(t => ({
              id: t.id,
              count: t.count,
              label: <Tag label={t.label} color={t.color} icon={t.icon} />,
              isChecked: selectedTags.map(el => el.id).includes(t.id),
            })),
          isExpanded: rememberedIsExpanded === null ? true : rememberedIsExpanded,
        };
      })
      .filter(el => !!el.items.length);
  }

  useEffect(() => {
    setFilterOptions(transformTagGroupsIntoTagOptions(tagGroups));
  }, [tagGroups, searchPhrase, selectedTags]);

  function handleCategoryClick(categoryId) {
    const newFilterOptions = [...filterOptions];
    const category = newFilterOptions.find(o => o.id === categoryId);
    category.isExpanded = !category.isExpanded;
    setFilterOptions(newFilterOptions);
    callLocalStorage(`tag-group-panel-expanded-${categoryId}`, "set", category.isExpanded);
  }

  function handleCheckboxClick(tagGroupId, itemId, isChecked) {
    const tag = tagGroups.find(el => el.id === tagGroupId).tags.find(el => el.id === itemId);

    let newSelectedTags = [...selectedTags];
    if (isChecked) {
      newSelectedTags = newSelectedTags.filter(el => el.id !== itemId);
    } else {
      newSelectedTags.push(tag);
    }

    setSelectedTags(newSelectedTags);
  }

  function clearFilter() {
    setSelectedTags([]);
  }

  async function addTag(groupId, tagData) {
    const group = tagGroups.find(el => el.id === groupId);
    const groupColor = getTagGroupColor(group);
    const newTag = {
      id: getNextId(tagGroups.map(el => el.tags).flat()) + 1, // dnd-kit identifiers must be higher than 0
      color: groupColor || "grey",
      groupId: groupId,
      count: 0,
      ...tagData,
    };
    const newGroups = [...tagGroups];
    newGroups.find(g => g.id === groupId).tags.push(newTag);

    try {
      await callApi("tags", "post", { tagGroups: newGroups });
    } catch (e) {
      console.log(e);
    }
    dispatch({ type: "UPDATE", tagGroups: newGroups });

    setSelectedTags([...selectedTags, newTag]);
  }

  const tagInputPopoverProps = {
    addTag,
    searchPhrase,
    setSearchPhrase,
    filterOptions,
    handleCategoryClick,
    handleCheckboxClick,
    clearFilter,
    isLoading,
    tagGroups,
  };

  if (isLoading) {
    return <Loader />;
  }

  return <TagInput selectedTags={selectedTags} tagInputPopoverProps={tagInputPopoverProps} />;
}

TagInputContainer.propTypes = {
  selectedTags: T.array,
  setSelectedTags: T.func,
};

export default TagInputContainer;
